/**
 * On-screen keyboard awareness for phones and tablets.
 *
 * A phone keyboard does not resize the page. It shrinks the VISUAL viewport
 * (the part of the page actually on screen) and leaves the layout viewport,
 * which is what `position: fixed`, `100vh` and a centred <dialog> are measured
 * against, at its full height. A form or a dialog laid out against the full
 * height therefore keeps its lower half behind the keyboard, with nothing to
 * scroll, and the field being typed into can be out of sight.
 *
 * This module publishes the visible area as CSS variables on <html> and marks
 * the document while a keyboard is up, so styles can lay out against what the
 * user can actually see:
 *
 *   --vvh     height of the visible area, in px
 *   --vv-top  how far the visible area is panned down the layout viewport, in px
 *   --kb      height of the layout viewport the keyboard covers, in px
 *             (0 where the browser resizes the page itself, see index.html)
 *   html.kb-open  present while a keyboard is up and a field has focus
 *
 * It also brings the focused field back into the visible area whenever the
 * keyboard opens or focus moves to a field that is out of sight.
 *
 * Consumers: components/modal.vue (dialogs) and App.vue (page and toasts).
 */

// A keyboard is never this short, and the browser's own bars collapsing on
// scroll never move the viewport this much.
const KEYBOARD_MIN_HEIGHT = 120;
// Keep a focused field this far from the edges of the visible area.
const REVEAL_MARGIN = 12;

let started = false;
// The tallest visible area seen at the current width: the viewport with no
// keyboard. A change of width (rotation, a resized window) starts it over.
let baseHeight = 0;
let baseWidth = 0;
let revealTimer: ReturnType<typeof setTimeout> | undefined;

const TEXT_INPUT_TYPES = new Set([
    "", "text", "search", "email", "url", "tel", "password", "number",
    "date", "datetime-local", "month", "time", "week",
]);

/**
 * Whether this element is a form field the keyboard belongs to. A <select>
 * counts: it raises no keyboard of its own, but focus reaches it from a text
 * field with the keyboard still up, and the layout has to stay fitted to the
 * visible area until the viewport actually grows back.
 */
function isEditable(el: Element | null): el is HTMLElement {
    if (!el || !(el instanceof HTMLElement)) return false;
    if (el.isContentEditable) return true;
    if (el instanceof HTMLSelectElement) return !el.disabled;
    if (el instanceof HTMLTextAreaElement) return !el.readOnly && !el.disabled;
    if (el instanceof HTMLInputElement) {
        return !el.readOnly && !el.disabled && TEXT_INPUT_TYPES.has((el.getAttribute("type") || "").toLowerCase());
    }
    return false;
}

function isTouchDevice(): boolean {
    return (navigator.maxTouchPoints || 0) > 0 || !!window.matchMedia?.("(pointer: coarse)").matches;
}

function visibleArea() {
    const vv = window.visualViewport;
    return {
        height: vv ? vv.height : window.innerHeight,
        width: vv ? vv.width : window.innerWidth,
        top: vv ? vv.offsetTop : 0,
        scale: vv ? vv.scale : 1,
    };
}

/** How far the middle of `el` is below the middle of the visible area, in px. */
function offsetFromVisibleMiddle(el: HTMLElement): number {
    const area = visibleArea();
    const rect = el.getBoundingClientRect();
    return rect.top + rect.height / 2 - (area.top + area.height / 2);
}

function isOutOfSight(el: HTMLElement): boolean {
    const area = visibleArea();
    const rect = el.getBoundingClientRect();
    return rect.top < area.top + REVEAL_MARGIN || rect.bottom > area.top + area.height - REVEAL_MARGIN;
}

/**
 * Scroll the focused field to the middle of the visible area, if it is outside
 * it. Not `scrollIntoView({ block: "center" })`: that centres on the layout
 * viewport, and with a keyboard covering more than half of the screen the
 * middle of the layout viewport is behind the keyboard.
 */
function revealFocused() {
    const el = document.activeElement;
    if (!isEditable(el) || !isOutOfSight(el)) return;

    // Nearest scroll container first (an open dialog, a scrolling panel), then
    // outwards, each taking up what the one inside it could not.
    for (let node = el.parentElement; node; node = node.parentElement) {
        if (node === document.body || node === document.documentElement) break;
        if (node.scrollHeight <= node.clientHeight + 1) continue;
        const overflowY = getComputedStyle(node).overflowY;
        if (overflowY !== "auto" && overflowY !== "scroll") continue;
        node.scrollTop += offsetFromVisibleMiddle(el);
        if (!isOutOfSight(el)) return;
    }
    // Not smooth: a second focus change mid-animation leaves the field short
    // of where it was going.
    window.scrollBy(0, offsetFromVisibleMiddle(el));
}

function scheduleReveal() {
    clearTimeout(revealTimer);
    // After the styles that depend on kb-open have been applied and the
    // keyboard has finished moving the viewport.
    revealTimer = setTimeout(revealFocused, 60);
}

function update() {
    const root = document.documentElement;
    const area = visibleArea();

    if (Math.abs(area.width - baseWidth) > 1) {
        baseWidth = area.width;
        baseHeight = area.height;
    } else if (area.height > baseHeight) {
        baseHeight = area.height;
    }

    // A pinch zoom shrinks the visible area too; that is not a keyboard.
    const covered = area.scale <= 1.01 ? baseHeight - area.height : 0;
    const open = isTouchDevice() && covered > KEYBOARD_MIN_HEIGHT && isEditable(document.activeElement);

    root.style.setProperty("--vvh", `${Math.round(area.height)}px`);
    root.style.setProperty("--vv-top", `${Math.round(area.top)}px`);
    root.style.setProperty("--kb", `${open ? Math.max(0, Math.round(window.innerHeight - area.height - area.top)) : 0}px`);

    const wasOpen = root.classList.contains("kb-open");
    root.classList.toggle("kb-open", open);
    if (open && !wasOpen) scheduleReveal();
}

function onFocusIn() {
    update();
    if (document.documentElement.classList.contains("kb-open")) scheduleReveal();
}

function onFocusOut() {
    // Focus moving from one field to the next fires focusout first, with
    // nothing focused for an instant. Wait for the focusin that follows
    // before deciding the keyboard is gone, or every Tab would flash the
    // layout back to full height.
    setTimeout(update, 0);
}

/** Start tracking. Safe to call more than once. */
export function initKeyboardInset() {
    if (started || typeof window === "undefined") return;
    started = true;

    const vv = window.visualViewport;
    if (vv) {
        vv.addEventListener("resize", update);
        vv.addEventListener("scroll", update);
    }
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);

    update();
}
